// S12 — el panel del equipo organizado por «¿qué me toca?» (docs/oficina-v2/S12-ESTADOS-BRIEF.md). Es LA lista
// de la oficina desde el 30-09 (escritorio y móvil; sustituyó a la de «Trabajando / Parados», que metía en
// «Parados» a quien esperaba la respuesta de Pau). La lógica (grupos, orden, frases, resumen) vive
// en situacion.ts; aquí solo se pinta. Cada situación lleva su icono y su color, iguales en la lista, la
// cabecera del chat y la franja: 1 (permiso) y 2 (no puede seguir) tiñen la fila; 3 (te ha escrito) es un
// chat normal: negrita + punto si no lo has leído, normal si ya lo leíste.
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronRight, CircleCheck, Clock, Handshake, MoreHorizontal, PanelLeftClose, Pin, Plus, Search, ShieldCheck } from 'lucide-react';
import { abrirSaltar } from './Saltar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO, Situacion } from '../compartido/vocabulario';
import { Boton, IconoEncargo } from '../ui';
import { oficinaApi } from '../api';
import { mensajeError } from '../errores';
import { useToastStore } from '../../../stores/toast.store';
import { useNoLeido } from '../leidos';
import { hace, quien, SUPERFICIE, ultimoMovimiento } from './estado';
import { BandejaDespliegues } from '../despliegues/BandejaDespliegues';
import { useOficinaStore } from '../store';
import type { PestanaBandeja } from './bandejas';
import { useBandeja } from './useBandeja';
import { agruparPorSituacion, aspecto, aspectoDe, aspectoGrupo, esperaDesde, etiquetaSituacion, fraseDeFila, haTerminado, leToca, resumenCabecera, seVa, sinNoticiasDesde, tituloGrupo, type Grupo, type SantiDelAgente } from './situacion';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { IsotipoVenuz } from '../ui/IsotipoVenuz';
import { LineaCuentasMovil } from '../cuentas/CuentasClaude';

/** El icono de la situación sobre su color (la misma insignia en la lista, el chat y la franja). */
export function InsigniaSituacion({ situacion, c, tamano = 18, className }: { situacion: Situacion; c?: Pick<ContratadoDTO, 'situacion' | 'pide'>; tamano?: number; className?: string }) {
  const a = c ? aspectoDe(c) : aspecto(situacion);
  const Icono = a.icono;
  // 7 · sin nada pendiente: sin insignia (apagado).
  if (!Icono) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full', a.insignia, className)} style={{ width: tamano, height: tamano }} aria-hidden>
      <Icono style={{ width: Math.round(tamano * 0.62), height: Math.round(tamano * 0.62) }} strokeWidth={2.5} />
    </span>
  );
}

/** Retrato con la insignia de su situación abajo a la derecha (sustituye al punto de color: nunca solo color). */
export function AvatarSituacion({ c, tamano = 44, anillo = 'ring-oficina-panel' }: { c: ContratadoDTO; tamano?: number; anillo?: string }) {
  return (
    <span className="relative inline-flex shrink-0">
      <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={tamano} />
      <InsigniaSituacion situacion={c.situacion} c={c} tamano={Math.max(20, Math.round(tamano * 0.46))} className={cn('absolute -bottom-0.5 -right-1 ring-2', anillo)} />
    </span>
  );
}

export function ListaEquipoS12({
  contratados,
  abierto,
  alAbrir,
  alContratar,
  alResaltar,
  alPlegar,
  conCuentas = false,
}: {
  contratados: Record<string, ContratadoDTO>;
  abierto: string | null;
  alAbrir: (agentId: string) => void;
  alContratar: () => void;
  alResaltar?: (agentId: string | null) => void;
  /** Móvil (22-10): la línea de las cuentas de Claude junto al resumen; en escritorio va el cartel de la pared. */
  conCuentas?: boolean;
  /** S13 — plegar la lista a una columna de caras (solo en la oficina de escritorio/iPad; en móvil no hay). */
  alPlegar?: () => void;
}) {
  // 06-10 — una bandeja por persona: cada uno ve sus agentes (los que contrató) y, con el selector, la de otro.
  const { mia, pestanas, clave, visibles: todos, sabido, vista } = useBandeja(contratados);
  const verBandeja = useOficinaStore((s) => s.verBandeja);
  // La que se miraba se quedó sin agentes: se vuelve a la propia (y no salta sola si ese dueño contrata otro).
  useEffect(() => {
    if (vista && vista !== clave) verBandeja(null);
  }, [vista, clave, verBandeja]);
  const ajena = pestanas.find((p) => p.clave === clave && !p.propia);
  const esNoLeido = (c: ContratadoDTO | undefined) => !!c?.noLeido;
  // El chat abierto se marca leído al abrirlo; sin esto, con «lo no leído primero» su fila saltaría debajo
  // mientras Pau la está leyendo. Mientras siga abierto, conserva el lugar que tenía al abrirlo.
  const [fijo, setFijo] = useState<{ id: string | null; noLeido: boolean }>({ id: null, noLeido: false });
  if (fijo.id !== abierto) setFijo({ id: abierto, noLeido: abierto ? esNoLeido(contratados[abierto]) : false });
  // 03-11: los contratos con Santi de cada agente (para el grupo «Con Santi» y la marca de la fila).
  const santi = useSantiPorAgente();
  const grupos = useMemo(
    () => agruparPorSituacion(todos, (c) => (c.agentId === fijo.id ? fijo.noLeido : c.noLeido), santi),
    [todos, fijo, santi],
  );
  const resumen = useMemo(() => resumenCabecera(todos), [todos]);

  return (
    <nav aria-label={t('oficinaV2.equipo.titulo')} className={cn('flex max-h-full flex-col overflow-hidden rounded-o-l', SUPERFICIE)}>
      <header className="px-4 pb-2 pt-3.5">
        <div className="flex items-center gap-3">
          {alPlegar ? (
            <button
              type="button"
              onClick={alPlegar}
              aria-label={t('oficinaV2.equipo.plegar')}
              title={t('oficinaV2.equipo.plegar')}
              className="-my-1 -ml-2 -mr-2 inline-flex h-11 w-11 items-center justify-center rounded-o-s text-oficina-texto2 transition-colors hover:bg-oficina-hover hover:text-oficina-texto focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
              data-testid="plegar-lista"
            >
              <PanelLeftClose className="h-[18px] w-[18px]" aria-hidden />
            </button>
          ) : null}
          <h1 className="flex flex-1 items-center gap-2 text-o-titulo font-bold text-oficina-texto"><IsotipoVenuz className="h-5 w-5 shrink-0" />{t('oficinaV2.titulo')}</h1>
          {/* 09-10 (Pau aprobó «Cuentas a un menú»): lo que se usa de vez en cuando va en «⋯», no a la vista junto a
              «Contratar». Cuentas personales: quién está dentro, cerrar sesiones, altas y el registro de quién hizo qué. */}
          <Menu.Root>
            <Menu.Trigger asChild>
              <button
                type="button"
                aria-label={t('oficinaV2.equipo.masOpciones')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-o-s text-oficina-texto2 transition-colors hover:bg-oficina-hover hover:text-oficina-texto focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70 data-[state=open]:bg-oficina-hover data-[state=open]:text-oficina-texto"
                data-testid="menu-equipo"
              >
                <MoreHorizontal className="h-[18px] w-[18px]" aria-hidden />
              </button>
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Content align="end" sideOffset={6} className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
                <Menu.Item asChild>
                  <a
                    href="/cuentas"
                    className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
                    data-testid="ir-a-cuentas"
                  >
                    <ShieldCheck className="h-4 w-4 text-oficina-texto2" aria-hidden />
                    {t('oficinaV2.equipo.cuentasYAccesos')}
                  </a>
                </Menu.Item>
              </Menu.Content>
            </Menu.Portal>
          </Menu.Root>
          <Boton variante="primario" tamano="sm" icono={Plus} onClick={alContratar}>
            {t('oficinaV2.contratarCorto')}
          </Boton>
        </div>
        {/* 03-11 (rebrand): el buscador de Spotlight (Ctrl/⌘+K) también a la vista, como el campo de búsqueda de Mensajes. */}
        <button
          type="button"
          onClick={abrirSaltar}
          className="mt-2.5 flex h-9 w-full items-center gap-2 rounded-o-m bg-oficina-control px-3 text-left text-o-texto text-oficina-texto3 outline-none transition-colors hover:bg-oficina-pulsado focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          data-testid="abrir-saltar"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{t('oficinaV2.saltar.placeholder')}</span>
          <kbd className="rounded-o-s border border-oficina-linea px-1.5 font-sans text-o-mini text-oficina-texto3">{t('oficinaV2.saltar.atajo')}</kbd>
        </button>
        {sabido && pestanas.length > 1 ? <SelectorBandeja pestanas={pestanas} clave={clave} alElegir={(c) => verBandeja(c === mia ? null : c)} /> : null}
        {ajena ? (
          <p className="mt-2 text-o-sub leading-5 text-oficina-texto2" data-testid="bandeja-ajena">
            {t('oficinaV2.s12.bandejas.mirando', { nombre: ajena.nombre })} ·{' '}
            <button type="button" onClick={() => verBandeja(null)} className="font-semibold text-oficina-enlace underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
              {t('oficinaV2.s12.bandejas.volver')}
            </button>
          </p>
        ) : null}
        {todos.length ? (
          <div className="mt-2 space-y-1">
            {/* 10-10 (Pau aprobó «menos cabecera»): lo que te toca ya NO se lista aquí — repetía, con las mismas
                palabras e iconos, los títulos de grupo que hay justo debajo y empujaba a la primera persona media
                pantalla abajo en el móvil. Solo queda «Nada te toca ahora» (no tiene grupo que lo diga) y la línea
                apagada de lo que pasa sin ti. */}
            {!resumen.teToca.length ? <p className="text-o-sub font-semibold leading-5 text-oficina-texto">{t('oficinaV2.s12.resumen.nadaTeToca')}</p> : null}
            {resumen.aparte.length || conCuentas ? (
              <div className="flex items-center justify-between gap-3">
                {resumen.aparte.length ? (
                  <p className="text-o-sub leading-5 text-oficina-texto2" data-testid="resumen-aparte">{resumen.aparte.map((p) => p.texto).join(' · ')}</p>
                ) : <span />}
                {conCuentas ? <LineaCuentasMovil /> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="min-h-0 overflow-y-auto px-2 py-2 [scrollbar-width:thin]">
        {/* Producción es una sola: las subidas pendientes salen en todas las bandejas. */}
        <BandejaDespliegues />
        {grupos.map(({ grupo, miembros, subgrupos }) => (
          <GrupoS12 key={grupo} grupo={grupo} n={miembros.length} titulo={grupo === 'te_escribio' ? tituloTeEscribio(miembros.filter(haTerminado).length) : undefined} resumen={grupo === 'con_santi' ? t('oficinaV2.s12.conSanti.resumen', { cuando: hace(miembros.map(ultimoMovimiento).sort()[0] ?? '') }) : undefined}>
            {subgrupos
              ? // Fijados: por dentro, por estado (06-10, Pau), con un subtítulo pequeño por estado.
                subgrupos.map((s) => (
                  <SubgrupoS12 key={s.grupo} grupo={s.grupo} n={s.miembros.length}>
                    {s.miembros.map((c) => (
                      <FilaS12 key={c.agentId} c={c} santi={santi.get(c.agentId)} activo={c.agentId === abierto} alAbrir={alAbrir} alResaltar={alResaltar} />
                    ))}
                  </SubgrupoS12>
                ))
              : miembros.map((c) => (
                  <FilaS12 key={c.agentId} c={c} santi={santi.get(c.agentId)} activo={c.agentId === abierto} alAbrir={alAbrir} alResaltar={alResaltar} />
                ))}
          </GrupoS12>
        ))}
        {!todos.length ? (
          <div className="flex flex-col items-center px-6 pb-6 pt-4 text-center">
            <h2 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.equipo.vacioTitulo')}</h2>
            <p className="mt-1.5 text-o-cuerpo leading-relaxed text-oficina-texto2">{t('oficinaV2.equipo.vacio')}</p>
          </div>
        ) : null}
      </div>
    </nav>
  );
}

/** Las bandejas: la propia («Mía») y las de los demás que tienen agentes, con cuántos esperan a su dueño. */
function SelectorBandeja({ pestanas, clave, alElegir }: { pestanas: PestanaBandeja[]; clave: string; alElegir: (clave: string) => void }) {
  return (
    <div role="group" aria-label={t('oficinaV2.s12.bandejas.selector')} className="mt-2 flex flex-wrap gap-1.5" data-testid="selector-bandeja">
      {pestanas.map((p) => {
        const activa = p.clave === clave;
        return (
          <button
            key={p.clave}
            type="button"
            aria-pressed={activa}
            onClick={() => alElegir(p.clave)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-o-sub transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
              activa ? 'bg-oficina-texto font-semibold text-oficina-fondo' : 'bg-white/[0.06] text-oficina-texto2 hover:bg-white/[0.1] hover:text-oficina-texto',
            )}
          >
            {p.propia ? t('oficinaV2.s12.bandejas.mia') : p.nombre}
            {!p.propia && p.teToca ? (
              <>
                <span aria-hidden className={cn('rounded-full px-1.5 text-o-mini font-semibold', activa ? 'bg-oficina-fondo/20' : 'bg-oficina-bloqueado/20 text-oficina-bloqueado')}>
                  {p.teToca}
                </span>
                <span className="sr-only">{t('oficinaV2.s12.bandejas.esperan', { n: p.teToca, nombre: p.nombre })}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** 09-10 — «Esperan tu respuesta (2 han terminado)»: cuántos te esperan porque han acabado, no porque pregunten. */
function tituloTeEscribio(terminados: number): string | undefined {
  if (!terminados) return undefined;
  return terminados === 1 ? t('oficinaV2.s12.teEscribioTerminados.uno') : t('oficinaV2.s12.teEscribioTerminados.varios', { n: terminados });
}

function GrupoS12({ grupo, n, titulo, resumen, children }: { grupo: Grupo; n: number; titulo?: string; resumen?: string; children: React.ReactNode }) {
  const a = aspectoGrupo(grupo);
  const Icono = a.icono;
  const apagado = grupo === 'en_marcha' || grupo === 'sin_pendiente' || grupo === 'se_van';
  // 29-10 (Pau): los que se van, al fondo y plegados: una línea apagada que se abre si quiere mirarlos.
  const [abierto, setAbierto] = useState(false);
  // 03-11: «Con Santi», plegado en una fila tranquila (lo tienen ellos; sin ruido) que se abre al pulsarla.
  if (grupo === 'con_santi') {
    return (
      <section className="mb-1.5" data-testid="grupo-con-santi">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="flex min-h-11 w-full items-center gap-2.5 rounded-o-m bg-oficina-control/60 px-3 py-2 text-left outline-none transition-colors hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
        >
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-oficina-santi/15 text-oficina-santi" aria-hidden>
            <Handshake className="h-4 w-4" strokeWidth={2.2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-o-texto font-semibold text-oficina-texto">
              {titulo ?? tituloGrupo(grupo)} <span className="font-medium tabular-nums text-oficina-texto3">{n}</span>
            </span>
            {resumen ? <span className="block truncate text-o-sub text-oficina-texto2">{resumen}</span> : null}
          </span>
          <ChevronRight className={cn('h-4 w-4 shrink-0 text-oficina-texto3 transition-transform duration-150 ease-o-apple motion-reduce:transition-none', abierto && 'rotate-90')} aria-hidden />
        </button>
        {abierto ? <ul className="mt-0.5 space-y-0.5">{children}</ul> : null}
      </section>
    );
  }
  if (grupo === 'se_van') {
    return (
      <section className="mb-1.5 opacity-70" data-testid="grupo-se-van">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="flex min-h-11 w-full items-center gap-1.5 rounded-o-s px-2 text-left text-o-sub font-semibold text-oficina-texto3 outline-none hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none', abierto && 'rotate-90')} aria-hidden />
          <span className="flex-1">{titulo ?? tituloGrupo(grupo)}</span>
          <span className="text-o-nota font-medium tabular-nums">{n}</span>
        </button>
        {abierto ? <ul className="space-y-0.5">{children}</ul> : null}
      </section>
    );
  }
  return (
    <section className="mb-1.5">
      <h2 className={cn('flex items-center gap-1.5 px-2 pb-1 pt-2 text-o-sub font-semibold', apagado ? 'text-oficina-texto2' : a.texto)}>
        {Icono ? <Icono className={cn('h-3.5 w-3.5 shrink-0', a.texto)} strokeWidth={2.5} aria-hidden /> : null}
        <span className="flex-1">{titulo ?? tituloGrupo(grupo)}</span>
        <span className="text-o-nota font-medium tabular-nums text-oficina-texto3">{n}</span>
      </h2>
      <ul className="space-y-0.5">{children}</ul>
    </section>
  );
}

/** Un estado dentro de «Fijados»: subtítulo más pequeño que el de grupo, con su icono y color, y sus filas. */
function SubgrupoS12({ grupo, n, children }: { grupo: Exclude<Grupo, 'fijados'>; n: number; children: React.ReactNode }) {
  const a = aspectoGrupo(grupo);
  const Icono = a.icono;
  const apagado = grupo === 'en_marcha' || grupo === 'sin_pendiente';
  return (
    <li>
      <h3 className={cn('flex items-center gap-1.5 pb-0.5 pl-4 pr-2 pt-1.5 text-o-nota font-medium', apagado ? 'text-oficina-texto2' : a.texto)}>
        {Icono ? <Icono className={cn('h-3 w-3 shrink-0', a.texto)} strokeWidth={2.5} aria-hidden /> : null}
        <span className="flex-1">{tituloGrupo(grupo)}</span>
        <span className="tabular-nums text-oficina-texto3">{n}</span>
      </h3>
      <ul className="space-y-0.5">{children}</ul>
    </li>
  );
}

export function FilaS12({ c, santi, activo, alAbrir, alResaltar }: { c: ContratadoDTO; santi?: SantiDelAgente; activo: boolean; alAbrir: (id: string) => void; alResaltar?: (id: string | null) => void }) {
  const a = aspectoDe(c);
  // 29-10: lo que se va no hace ruido (sin punto de no leído).
  const noLeido = useNoLeido(c) && !seVa(c);
  const urgente = leToca(c);
  // 09-10 — un fijado al que le toca algo a Pau sale en su grupo de urgencia, no en «Fijados»: la chincheta lo dice.
  const fijadoFuera = c.fijado && urgente;
  // Como un chat: lo que le toca a Pau y aún no ha leído (el «no leído» del servidor) = negrita + punto.
  const sinLeer = noLeido && !activo;
  // «hace X» siempre: en 1-3, desde cuándo te espera; en el resto, su último movimiento.
  const cuando = hace(urgente ? esperaDesde(c) : ultimoMovimiento(c));
  // 09-10 — trabaja pero lleva rato callado: en vez de la hora, «sin noticias · 25 min» en ámbar.
  const sinNoticias = sinNoticiasDesde(c);
  const terminado = haTerminado(c);
  // La sala solo si hay más de una (con una sola, sobra).
  const sala = useOficinaStore((s) => (s.salas.length > 1 ? s.salas.find((x) => x.projectId === c.projectId) : undefined));
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (activo) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [activo]);
  // 09-10 (Pau aprobó «aprobar desde la lista»): el servidor dice si hay UN permiso sencillo que se aprueba aquí.
  const rapido = c.situacion === 'autorizacion' ? c.permisoRapido : null;
  return (
    <li className="flex items-center">
      <button
        ref={ref}
        type="button"
        onClick={() => alAbrir(c.agentId)}
        // Solo con ratón (06-10, Pau en el iPad): al tocar, el iPad deja un «ratón» de mentira encima de la fila
        // y la tarjeta del mundo se quedaba fija sobre el chat recién abierto. Igual que las mesas del mundo.
        onPointerEnter={(e) => e.pointerType === 'mouse' && alResaltar?.(c.agentId)}
        onPointerLeave={(e) => e.pointerType === 'mouse' && alResaltar?.(null)}
        aria-current={activo ? 'true' : undefined}
        aria-label={`${quien(c)}${sala ? ` (${sala.nombre})` : ''}${fijadoFuera ? `, ${t('oficinaV2.s12.fijado').toLowerCase()}` : ''}. ${etiquetaSituacion(c.situacion)}. ${fraseDeFila(c)}${sinNoticias ? `. ${sinNoticias}` : ''}`}
        title={quien(c)}
        className={cn(
          'flex w-full min-w-0 flex-1 items-start gap-3 rounded-o-m py-2 pl-2.5 pr-2 text-left outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
          // 03-11 (Apple): sin barra lateral; lo que te para va teñido. La seleccionada, en el gris de selección de macOS:
          // nunca en oro (10-10, Pau: el oro solo significa «actúa»).
          activo ? 'bg-oficina-pulsado' : (a.fila ?? 'hover:bg-white/[0.05]'),
        )}
      >
        <AvatarSituacion c={c} tamano={40} anillo={activo ? 'ring-oficina-pulsado' : 'ring-oficina-elevada'} />
        <span className="min-w-0 flex-1">
          {/* 10-10 (Pau aprobó «el título entero»): el título usa TODO el ancho, hasta 2 líneas; solo el punto de sin
              leer comparte su línea (arriba a la derecha). Antes la hora y la chincheta iban a su lado y en 304 px lo
              cortaban a ~15 letras («Avisar a las a…»), y el título es como Pau reconoce a cada agente. La hora y la
              chincheta bajan a la línea de la frase, delante, junto a la sala (que ya iba abajo por lo mismo: 09-10,
              en 304 px junto al título lo cortaba). El título entero sigue en `title`. */}
          <span className="flex items-start gap-2">
            <span data-testid="fila-titulo" className={cn('line-clamp-2 min-w-0 flex-1 break-words text-o-cuerpo leading-5 text-oficina-texto', sinLeer || urgente ? 'font-semibold' : 'font-medium')}>{quien(c)}</span>
            {sinLeer ? <span className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full bg-oficina-texto" aria-label={t('oficinaV2.s12.sinLeer')} /> : null}
          </span>
          <span
            className={cn(
              'mt-0.5 block text-o-sub leading-[18px]',
              // 1-2: lo que pide es el motivo para pulsar → hasta 2 líneas. El resto, 1.
              a.fila ? 'line-clamp-2' : 'line-clamp-1',
              sinLeer ? 'font-semibold text-oficina-texto' : a.fila ? cn('font-medium', a.textoFila) : a.textoFila,
            )}
          >
            {/* Delante de la frase, apagado: sala (si hay varias) · hora · chincheta. */}
            <span className="font-normal text-oficina-texto2" data-testid="fila-meta">
              {sala ? (
                <span data-testid="sala-fila">
                  <span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: sala.color }} aria-hidden />
                  {sala.nombre} ·{' '}
                </span>
              ) : null}
              {sinNoticias ? (
                <span className="text-o-nota tabular-nums text-oficina-bloqueado" data-testid="sin-noticias">
                  <Clock className="mr-1 inline h-3 w-3 align-[-1px]" strokeWidth={2.5} aria-hidden />
                  {sinNoticias}
                </span>
              ) : (
                <span className={cn('text-o-nota tabular-nums', sinLeer && 'font-semibold text-oficina-texto')} data-testid="fila-hora">{cuando}</span>
              )}
              {fijadoFuera ? <Pin className="ml-1 inline h-3 w-3 align-[-1px] text-oficina-texto2" strokeWidth={2.5} aria-hidden data-testid="fila-fijado" /> : null}
              {santi ? <Handshake className="ml-1 inline h-3.5 w-3.5 align-[-2px] text-oficina-santi" strokeWidth={2.2} aria-label={t('oficinaV2.s12.conSanti.marca')} data-testid="fila-santi" /> : null}
              {' · '}
            </span>
            {terminado ? <CircleCheck className="mr-1 inline h-3.5 w-3.5 align-[-2px]" strokeWidth={2.5} aria-hidden data-testid="fila-terminado" /> : null}
            {fraseDeFila(c)}
          </span>
        </span>
      </button>
      {/* Hermano de la fila, no dentro (un botón no puede ir dentro de otro): pulsarlo no abre el chat. */}
      {rapido ? <SiRapido c={c} permisoId={rapido.id} /> : null}
    </li>
  );
}

/**
 * El «Sí» de la lista: aprueba el permiso sin abrir el chat (oro: es actuar). Mientras va, girando y quieto; si
 * falla, un aviso con el motivo. Al salir bien no hace falta nada más: el agente deja de pedir permiso y la fila se
 * va de «Piden tu permiso» sola (llega por el socket); hasta entonces el botón no vuelve, para no aprobar dos veces.
 */
export function SiRapido({ c, permisoId }: { c: ContratadoDTO; permisoId: string }) {
  const [estado, setEstado] = useState<{ id: string; fase: 'enviando' | 'hecho' } | null>(null);
  const actual = estado?.id === permisoId ? estado.fase : null;
  if (actual === 'hecho') return null;
  const aprobar = async () => {
    setEstado({ id: permisoId, fase: 'enviando' });
    try {
      await oficinaApi.responderPermiso(c.agentId, permisoId, 'aprobar');
      setEstado({ id: permisoId, fase: 'hecho' });
    } catch (e) {
      setEstado(null);
      useToastStore.getState().push({ message: t('oficinaV2.s12.rapido.error', { nombre: quien(c), motivo: mensajeError(e) }) });
    }
  };
  return (
    <Boton
      variante="primario"
      cargando={actual === 'enviando'}
      onClick={() => void aprobar()}
      aria-label={t('oficinaV2.s12.rapido.aria', { nombre: quien(c), que: fraseDeFila(c) })}
      title={t('oficinaV2.s12.rapido.pista')}
      // Al lado de la fila, en su propio hueco (encima de ella pisaba la hora). 32 px; el área táctil llega a 44 (::before).
      className="relative ml-1 mr-2 h-8 min-w-11 px-3 text-o-sub before:absolute before:-inset-1.5 before:content-['']"
      data-testid="si-rapido"
    >
      {t('oficinaV2.s12.rapido.si')}
    </Boton>
  );
}
